/**
 * Generic 4-step "how it works" modal — controlled, button-triggered.
 * Caller supplies the resolved step copy + icons; no persistence, no auto-open.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { LucideIcon } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog.js';

export interface HowItWorksStep {
  icon: LucideIcon;
  title: string;
  body: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  steps: HowItWorksStep[];
}

const GRADIENTS = ['var(--art-1)', 'var(--art-2)', 'var(--art-4)', 'var(--art-6)'];

export function HowItWorksModal({ open, onClose, steps }: Props) {
  const { t } = useTranslation('promo');
  const [step, setStep] = useState(1);
  const total = steps.length;

  const finish = () => {
    onClose();
    setStep(1);
  };

  const current = steps[step - 1];
  const StepIcon = current.icon;

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) finish();
      }}
    >
      <DialogContent hideClose className="max-w-[320px] gap-0 overflow-hidden p-0">
        <div
          className="p-5 pb-4 text-white relative"
          style={{ background: GRADIENTS[(step - 1) % GRADIENTS.length] }}
        >
          <div className="flex items-center justify-between">
            <span className="px-2.5 py-0.5 rounded-xl text-[10px] font-bold uppercase tracking-wide bg-white/20">
              {step} / {total}
            </span>
            <button onClick={finish} className="text-white/70 text-[11px]">
              {t('tutorial.actions.skip')}
            </button>
          </div>
          <div className="mt-3" style={{ filter: 'drop-shadow(0 2px 6px rgba(0,0,0,.35))' }}>
            <StepIcon size={36} strokeWidth={2.25} />
          </div>
          <DialogTitle className="text-[16px] font-extrabold mt-2 leading-tight text-white">
            {current.title}
          </DialogTitle>
          <div className="text-[11px] opacity-90 mt-1">{current.body}</div>
        </div>

        <div className="bg-[var(--bg-raised)] p-4 text-[var(--text-primary)]">
          <div className="flex gap-1.5 justify-center my-3">
            {steps.map((_, i) => (
              <div
                key={i}
                className={[
                  'h-[5px] rounded-full transition-all',
                  i + 1 === step
                    ? 'w-6 bg-white shadow-[0_0_8px_rgba(255,255,255,.4)]'
                    : 'w-4 bg-[var(--bg-elevated)]',
                ].join(' ')}
              />
            ))}
          </div>

          <div className="flex gap-2">
            <button
              onClick={() => setStep((s) => Math.max(1, s - 1))}
              disabled={step === 1}
              className="flex-1 rounded-lg py-2.5 font-bold text-[12px] bg-[var(--bg-elevated)] text-[var(--text-secondary)] disabled:opacity-40"
            >
              {t('tutorial.actions.back')}
            </button>
            {step < total ? (
              <button
                onClick={() => setStep((s) => s + 1)}
                className="flex-[2] rounded-lg py-2.5 font-bold text-[12px] text-[#1f2937] bg-[#fbbf24] shadow-[0_4px_12px_rgba(251,191,36,.35)]"
              >
                {t('tutorial.actions.next')}
              </button>
            ) : (
              <button
                onClick={finish}
                className="flex-[2] rounded-lg py-2.5 font-bold text-[12px] text-white bg-[var(--success)] shadow-[0_4px_12px_rgba(16,185,129,.4)]"
              >
                {t('tutorial.actions.finish')}
              </button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
